import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { createChartXAxisLabel } from "utils/charts";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetEMSMemberEnergyQuery } from "state/communities/communitiesApiSlice";

import EmsChartCard from "./EmsChartCard";

/* Chart 4 — community grid import (kWh) over the day, with the solar available
   overlaid. Import drops midday (solar + batteries) and rises at night. */
function PromoterRetailChart({ moduleType, startDatetime, endDatetime, onDataStatus }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const { data: rawList = [], isLoading } = useGetEMSMemberEnergyQuery(
    { communityId, moduleType, startDatetime, endDatetime, limit: 5000, offset: 0 },
    { skip: !communityId }
  );

  const hasData = !isLoading && rawList.length > 0;
  const isEmpty = !isLoading && !hasData;
  useEffect(() => {
    if (!isLoading && onDataStatus) onDataStatus(!isEmpty);
  }, [isLoading, isEmpty, onDataStatus]);

  const { series, labels } = useMemo(() => {
    if (!hasData) return { series: [], labels: [] };
    const imp = new Map();
    const exp = new Map();
    const avail = new Map();
    const tsSet = new Set();
    rawList.forEach((r) => {
      const ts = r.datetime_timestamp;
      if (!ts) return;
      tsSet.add(ts);
      imp.set(ts, (imp.get(ts) || 0) + (r.e_sup || 0));
      exp.set(ts, (exp.get(ts) || 0) + (r.e_sur || 0));
      if (r.metadata?.e_avail != null && !avail.has(ts)) avail.set(ts, Number(r.metadata.e_avail));
    });
    const sorted = [...tsSet].sort((a, b) => new Date(a) - new Date(b));
    const hasExport = [...exp.values()].some((v) => v > 0.001);
    const result = [
      {
        name: t("unified_ems.grid_import"),
        data: sorted.map((ts) => Number((imp.get(ts) || 0).toFixed(3))),
        color: "var(--danger)",
      },
      {
        name: t("unified_ems.promoter_solar_avail"),
        data: sorted.map((ts) => Number((avail.get(ts) || 0).toFixed(3))),
        color: "var(--warning)",
      },
    ];
    if (hasExport) {
      result.push({
        name: t("unified_ems.grid_export"),
        data: sorted.map((ts) => Number((exp.get(ts) || 0).toFixed(3))),
        color: "var(--success)",
      });
    }
    return { series: result, labels: sorted.map((ts) => createChartXAxisLabel(ts, "MIN15")) };
  }, [rawList, hasData, t]);

  if (isLoading || isEmpty) return null;
  const legend = series.map((s) => ({ label: s.name, color: s.color }));

  return (
    <EmsChartCard
      title={t("unified_ems.promoter_retail_chart_title")}
      sub={t("unified_ems.promoter_retail_chart_sub")}
      legend={legend}
      series={series}
      labels={labels}
      height={420}
      unit="kWh"
      yTitle="kWh"
      type="area"
    />
  );
}

PromoterRetailChart.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
  onDataStatus: undefined,
};
PromoterRetailChart.propTypes = {
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
  onDataStatus: PropTypes.func,
};

export default PromoterRetailChart;
